import { PessoaSelect } from "@/Components/Populados/Pessoa/PessoaSelect";
import { EnumPessoaTipo } from "@/Models/Pessoa/Pessoa";
import { Form, FormProps, Row, Col, Input, Collapse, InputNumber, Button, Select, Switch, notification, Tag, FormInstance, Divider } from "antd";
import TituloSituacaoSelect from "../../TituloSituacao/TituloSituacaoSelect";
import InputDate from "@/Components/Base/Input/InputDate";
import InputPreco from "@/Components/Base/Input/InputPreco";
import { PlanoDeContasSelectItem } from "../../../PlanoDeContas/PlanoDeContasSelect";
import { CentroCustoSelectItem } from "../../../CentroConta/CentroCustoSelect";
import { forwardRef, ReactNode, useEffect, useMemo, useRef } from "react";
import ViagemSelect from "@/Components/Populados/Viagem/ViagemSelect";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import TituloRaterioFormItem from "../../Rateio/TituloRateioFormItem";
import ContaSelect from "../../../Conta/ContaSelect";
import { EnumTipoConta } from "@/Models/Financeiro/Conta";
import Rateio from "@/Models/Financeiro/Titulo/Rateio";
import MovFormaPagamentoSelect from "../../../Conta/Movimentacao/MovFormaPagamento/MovFormaPagamentoSelect";
import dayjs from "dayjs";
import { EnumTituloSituacao } from "@/Models/Financeiro/Titulo/TituloSituacao";
import { LancamentoType } from "@/Services/Financeiro/Titulo/ParcelaService";
import InscricaoResponsavelSelect from "@/Components/Populados/Viagem/Inscricao/InscricaoResponsavelSelect";
import { EnumPlanoContasTipoMovimentacao } from "@/Models/Financeiro/PlanoContasTipo";
import { EnumRegistroTipo } from "@/Models/Financeiro/PlanoContasTipoRegistro";
import { EnumOrigemTipo } from "@/Models/Financeiro/Titulo/Origem/OrigemTipo";
import Titulo, { EnumTituloTipo } from "@/Models/Financeiro/Titulo/Titulo";
import { EnumMovFormaPagamento } from "@/Models/Financeiro/Conta/Movimentacao/MovFormaPagamento";
import InputCnpj from "@/Components/Populados/Pessoa/Documento/InputCnpj";
import { usePermitido } from "@/Components/Base/Permissao/ComponentePermissao";
import TituloReembolsoFormModal from "../../TituloReembolsoFormModal";
import LancamentoVerificarInscricaoModal from "../../LancamentoVerificarInscricaoModal";

export interface IAReceberLancamentoFormBasicProps extends Omit<FormProps<LancamentoType>, "children"> {
    children ?: ReactNode;
    onSuccessReembolsar?:(titulo:Titulo) => void;
}

const AReceberLancamentoFormBasic = forwardRef<FormInstance<LancamentoType>, IAReceberLancamentoFormBasicProps>(function AReceberLancamentoFormBasic(
    {
        form: formParam,
        children,
        initialValues,
        layout,
        disabled,
        onSuccessReembolsar,
        ...rest
    },
    ref
) {
    const [newForm] = Form.useForm<LancamentoType>();
    const form = formParam ?? newForm;
    const refChildrenForm = useRef<HTMLDivElement>(null);
    const {
        permitido : permitidoInserirRestrito
    } = usePermitido({
        permissoes : ["FinanceiroTituloAcessoExclusivoInserir"]
    });

    const tituloCodigoFormValue = initialValues?.titulo?.codigo;
    const isAlteracao = tituloCodigoFormValue !== undefined && isFinite(tituloCodigoFormValue) && !isNaN(tituloCodigoFormValue) && tituloCodigoFormValue > 0;

    const emitenteCadastradoFormValue = Form.useWatch(["emitenteCadastrado"], form);

    const codFaturaFormValue = Form.useWatch(["titulo", "codFatura"], form);
    const codTipoFormValue = Form.useWatch(["titulo", "codTipo"], form);
    const tituloCodFaturaReembolsoFormValue = Form.useWatch(["titulo", "codFaturaReembolso"], form);

    const codDevedorFormValue = Form.useWatch(["titulo", "codDevedor"], form);
    const codDevedorTipoFormValue = Form.useWatch<EnumPessoaTipo|undefined>(["titulo", "devedor", "tipo"], form);

    const codCentroCustoFormValue = Form.useWatch(["codCentroCusto"], form);
    const codPlanoDeContasFormValue = Form.useWatch(["codPlanoDeContas"], form);

    const tituloRateiosFormValue = Form.useWatch<Rateio[]>(["titulo", "rateios"], form);
    
    const tituloDataVencimentoFormValue = Form.useWatch<dayjs.Dayjs>(["titulo", "dataVencimento"], form);
    const tituloDataPagamentoFormValue = Form.useWatch<dayjs.Dayjs>(["titulo", "dataPagamento"], form);
    const tituloDataCreditoFormValue = Form.useWatch<dayjs.Dayjs>(["titulo", "dataCredito"], form);
    const dataPagamentoInformada = tituloDataPagamentoFormValue && tituloDataPagamentoFormValue.isValid();
    const tituloContaCreditoFormValue = Form.useWatch(["titulo", "movimentacao", "codConta"], form);
    
    
    const habilitarReembolso = isAlteracao &&  codTipoFormValue == EnumTituloTipo.Titulo && (tituloCodFaturaReembolsoFormValue ?? 0) <= 0;

    const codTituloMovFormaPagamentoFormValue = Form.useWatch<EnumMovFormaPagamento|undefined>(["titulo", "movimentacao", "codMovFormaPagamento"], form);
    
    const tituloValorBaseFormValue = Form.useWatch(["titulo", "valorBase"], form);
    const tituloValorDescontoFormValue = Form.useWatch(["titulo", "valorDesconto"], form);
    const tituloValorJurosMultaFormValue = Form.useWatch(["titulo", "movimentacao", "valorMulta"], form);
    const tituloValorTarifaFormValue = Form.useWatch(["titulo", "movimentacao", "tituloTarifa", "valorRecebido"], form);
    const tituloValorRecebidoFormValue = Form.useWatch(["titulo", "valorRecebido"], form);
    
    const codInscricaoFormValue = Form.useWatch(["codInscricao"], form);
    const codTituloAtualFormValue = (codFaturaFormValue ?? 0) > 0 ? codFaturaFormValue : (codTipoFormValue === EnumTituloTipo.Titulo ? tituloCodigoFormValue : undefined);
    // const numeroRepeticaoFormValue = Form.useWatch(["repetirLancamento", "numeroRepeticao"], form);
    const numeroRepeticaoParcelamentoFormValue = Form.useWatch(["parcelamento", "numeroRepeticao"], form);

    const valorPrevisto = useMemo(() => {
        const value : number = (tituloValorBaseFormValue ?? 0) - (tituloValorDescontoFormValue ?? 0);
        
        if (value < 0) return 0;

        return value;
    }, [tituloValorBaseFormValue, tituloValorDescontoFormValue]);

    const valorParcelado = useMemo(() => {
        const value = valorPrevisto / numeroRepeticaoParcelamentoFormValue;

        return isNaN(valorPrevisto) ? undefined : value;
    }, [numeroRepeticaoParcelamentoFormValue, valorPrevisto]);

    const valorCredito = useMemo(() => {
        if (dataPagamentoInformada)
            return (tituloValorRecebidoFormValue ?? 0) + (tituloValorJurosMultaFormValue ?? 0) - (codTituloMovFormaPagamentoFormValue === EnumMovFormaPagamento.Boleto ? 0 : (tituloValorTarifaFormValue ?? 0));
        return undefined;
    }, [tituloValorRecebidoFormValue, dataPagamentoInformada, tituloValorJurosMultaFormValue, tituloValorTarifaFormValue, codTituloMovFormaPagamentoFormValue]);

    const codSituacao = useMemo(() => {
        if (tituloDataCreditoFormValue && tituloDataCreditoFormValue.isValid()) return EnumTituloSituacao.Liquidado;

        return EnumTituloSituacao.Pendente;
    }, [tituloDataCreditoFormValue]);
    
    const rateios = useMemo(() => {
        if ((codCentroCustoFormValue !== undefined && codCentroCustoFormValue > 0) || (codPlanoDeContasFormValue !== undefined && codPlanoDeContasFormValue > 0))
        {
            const rateio = {
                codCentroCusto : codCentroCustoFormValue ?? 0,
                codPlanoConta : codPlanoDeContasFormValue ?? 0,
                percentual : 100
            } as Rateio;
            return [rateio] as Rateio[];
        }
        else 
        {
            return tituloRateiosFormValue;
        }
    }, [codCentroCustoFormValue, codPlanoDeContasFormValue, tituloRateiosFormValue]);

    const rateiosPreenchidos = useMemo(() => {
        return rateios?.some?.(any => any !== undefined && any.codCentroCusto !== undefined && any.codCentroCusto > 0) ?? false;
    }, [rateios]);

    useEffect(() => {
        form.setFieldValue(["repetirLancamento", "valorPrevisto"], valorPrevisto);
        form.setFieldValue(["parcelamento", "valorPrevisto"], valorPrevisto);
    }, [valorPrevisto]);

    useEffect(() => {
        form.setFieldValue(["repetirLancamento", "diaRepeticao"], tituloDataVencimentoFormValue?.date?.());
        form.setFieldValue(["parcelamento", "diaParcela"], tituloDataVencimentoFormValue?.date?.());
    }, [tituloDataVencimentoFormValue]);

    const initValuesRateios = ((initialValues?.titulo?.codFatura ?? 0) > 0 ? initialValues?.titulo?.fatura?.rateios : initialValues?.titulo?.rateios) ?? [];
    const initValues = {
        ...(initialValues ?? {}),
        codCentroCusto : initialValues?.codCentroCusto ?? ((initValuesRateios?.length ?? 0) === 1 ? initValuesRateios?.[0]?.codCentroCusto : undefined),
        codPlanoDeContas : initialValues?.codPlanoDeContas ?? ((initValuesRateios?.length ?? 0) === 1 ? initValuesRateios?.[0]?.codPlanoConta : undefined),
        codInscricao : initialValues?.codInscricao ?? (initialValues?.titulo?.origem?.codTipo === EnumOrigemTipo.Inscricao ? initialValues?.titulo?.origem?.codReferencia : undefined),
        codViagem : initialValues?.codViagem ?? (initialValues?.titulo?.origem?.codTipo === EnumOrigemTipo.Viagem ? initialValues?.titulo?.origem?.codReferencia : undefined),
        emitenteCadastrado : initialValues?.emitenteCadastrado === false ? false : true,
        titulo : {
            ...((initialValues as any)?.titulo ?? {}),
            codTipo : initialValues?.titulo?.codTipo ?? EnumTituloTipo.Titulo,
            descricao : initialValues?.titulo?.descricao ?? initialValues?.titulo?.movimentacao?.descricao,
            dataEmissao: initialValues?.titulo?.dataEmissao ? dayjs(initialValues?.titulo?.dataEmissao).startOf('day') : dayjs(),
            dataVencimento: initialValues?.titulo?.dataVencimento ? dayjs(initialValues?.titulo?.dataVencimento).startOf('day') : dayjs(),
            dataCriacao: initialValues?.titulo?.dataCriacao ? dayjs(initialValues?.titulo?.dataCriacao).startOf('day') : dayjs(),
            dataInclusao : initialValues?.titulo?.dataCriacao ? dayjs(initialValues?.titulo?.dataCriacao).startOf('day') : dayjs(),
            dataPagamento: initialValues?.titulo?.dataPagamento ? dayjs(initialValues?.titulo?.dataPagamento).startOf('day') : undefined,
            dataCredito: initialValues?.titulo?.dataCredito ? dayjs(initialValues?.titulo?.dataCredito).startOf('day') : undefined,
            valorRecebido: (initialValues?.titulo?.valorRecebido ?? 0) > 0 
                ? initialValues?.titulo?.valorRecebido - ((initialValues?.titulo?.movimentacao?.valorMulta ?? 0) + (initialValues?.titulo?.movimentacao?.valorJuros ?? 0))
                : undefined 
            ,
            movimentacao : (initialValues?.titulo?.movimentacao ?? undefined) ? {
                ...initialValues?.titulo?.movimentacao,
                dataMovimentacao : initialValues?.titulo?.movimentacao?.dataMovimentacao ? dayjs(initialValues?.titulo?.movimentacao?.dataMovimentacao).startOf('day') : undefined,
                dataCricao : initialValues?.titulo?.movimentacao?.dataCriacao ? dayjs(initialValues?.titulo?.movimentacao?.dataCriacao).startOf('day') : undefined,
                valorMulta : (initialValues?.titulo?.movimentacao?.valorMulta ?? 0) + (initialValues?.titulo?.movimentacao?.valorJuros ?? 0),
                valorTransacao : (initialValues?.titulo?.movimentacao?.valorTransacao ?? 0) > 0 
                    ? initialValues?.titulo?.movimentacao?.valorTransacao - ((initialValues?.titulo?.movimentacao?.valorMulta ?? 0) + (initialValues?.titulo?.movimentacao?.valorJuros ?? 0))
                    : undefined             
            } : undefined,
            rateios : (initValuesRateios?.length ?? 0) === 0 ? [ { percentual : 100 } ] : initValuesRateios,
            reembolsavel : initialValues?.titulo?.reembolsavel === false ? false : true,
            restrito : initialValues?.titulo?.restrito === true ? true : false
        }
    };

    return <Form
        {...rest}
        ref={ref}
        form={form}
        disabled={disabled}
        layout={layout ?? "vertical"}
        size={"small"}
        labelCol={{
            style : {
                fontWeight : "bold"
            }
        }}
        initialValues={initValues}
    >
        <Row gutter={8} ref={refChildrenForm}>
            <Form.Item label="Código" hidden name={["titulo", "codigo"]}>
                <Input disabled hidden />
            </Form.Item>
            <Form.Item label="Código Reembolso" hidden name={["titulo", "codFaturaReembolso"]}>
                <Input disabled hidden />
            </Form.Item>
            <Form.Item label="Cód Tipo" hidden name={["titulo", "codTipo"]} initialValue={EnumTituloTipo.Titulo}>
                <Input disabled hidden />
            </Form.Item>
            <Form.Item label="Cód Fatura" hidden name={["titulo", "codFatura"]}>
                <Input disabled hidden />
            </Form.Item>
            <Form.Item label="Cód Movimentação" hidden name={["titulo", "codMovimentacao"]}>
                <Input disabled hidden />
            </Form.Item>
            <Form.Item label="Emitente" hidden name={["titulo", "beneficiario", "tipo"]}>
                <Input disabled hidden />
            </Form.Item>
            <Form.Item label="Emitente" hidden name={["titulo", "devedor", "tipo"]}>
                <Input disabled hidden />
            </Form.Item>
            {emitenteCadastradoFormValue === false && <Form.Item label="Emitente" rules={[ { required : true, message : "Não foi possível consultar o cnpj informado." } ]} hidden name={["fornecedorNovo"]}>
                <Input disabled hidden />
            </Form.Item>}
            <Col xs={24} style={{ gap: 2, textAlign : "end" }}>
                {
                    habilitarReembolso
                    && <TituloReembolsoFormModal 
                        codFatura={tituloCodigoFormValue} 
                        onSuccess={onSuccessReembolsar}
                        buttonOpen={<Button 
                            type="primary" 
                            loading={false} 
                            danger 
                            htmlType="button" 
                            style={{ marginRight: 6 }}
                        >
                            Criar reembolso
                        </Button>} 
                    />
                }
                <Tag color="blue">
                    {isAlteracao ? "" : "Lançamento de"} {codTipoFormValue === EnumTituloTipo.Titulo ? "Título" : "Parcela"}
                </Tag>
            </Col>
            <Col xs={24} sm={24} md={11} lg={6}>
                <Form.Item label="Emitente" name={["titulo", "codBeneficiario"]} rules={[ {required : true } ]}>
                    <PessoaSelect 
                        tipoPessoa={EnumPessoaTipo.EMPRESA}
                        allowClear={false}
                    />
                </Form.Item>
            </Col>
            {/* <Col xs={24} sm={12} md={8} lg={5}>
                <Form.Item name={["titulo", "codDadosBancarioPagamento"]} label="Pix/C.C.">
                    <DadosBancarioPorPessoaSelect
                        filtros={{
                            codPessoa : codDevedorFormValue
                        }}
                    />
                </Form.Item>
            </Col> */}
            <Col xs={24} sm={24} md={13} lg={8}>
                <Row gutter={8} wrap={false}>
                    {
                        isAlteracao === false && <Col flex="none">
                            <Form.Item label={"Inscrito"} name={["emitenteCadastrado"]} tooltip={{ title : "Indica se o Destinatário já está cadastrado no sistema." }}>
                                <Switch checkedChildren="Sim" unCheckedChildren="Não" />
                            </Form.Item>
                        </Col>
                    }
                    <Col flex="auto">
                        {
                            (emitenteCadastradoFormValue === false && isAlteracao === false) && <Form.Item 
                                label="Destinatário" 
                                name={["_interno_cnpjBeneficiario"]} 
                                rules={[ {required : true } ]}
                            >
                                <InputCnpj 
                                    onChangePessoa={(pessoa) => {
                                        form.setFieldValue(["fornecedorNovo"], pessoa);
                                    }}
                                    onErroAoBuscarDados={() => {
                                        form.setFieldValue(["_interno_cnpjBeneficiario"], undefined);
                                        form.setFieldValue(["fornecedorNovo"], undefined);
                                    }}
                                />
                            </Form.Item> || <Form.Item label="Destinatário" name={["titulo", "codDevedor"]} rules={[ {required : true } ]}>
                                <PessoaSelect
                                    tipoPessoa={undefined}
                                    tiposPessoaInValidos={[EnumPessoaTipo.EMPRESA]}
                                    onChange={(_, record) => {
                                        form.setFieldValue(["titulo", "devedor", "tipo"], record?.tipo);
                                        form.setFieldValue(["codViagem"], undefined);
                                        form.setFieldValue(["codInscricao"], undefined);
                                    }}
                                    onSelect={(e) => {
                                        console.log("onSelect: ", e);
                                    }}
                                    allowClear={false}
                                />
                            </Form.Item>
                        }
                    </Col>
                </Row>
            </Col>
            <Col xs={6} sm={3} md={3} lg={2}>
                <Form.Item
                    label={"Remb."}
                    name={["titulo", "reembolsavel"]}
                    rules={[{ required: true }]}
                >
                    <Switch checkedChildren="Sim" unCheckedChildren="Não" />
                </Form.Item>
            </Col>
            {permitidoInserirRestrito && <Col xs={6} sm={3} md={3} lg={2}>
                <Form.Item
                    label={"Restrito"}
                    name={["titulo", "restrito"]}
                    rules={[{ required: true }]}
                >
                    <Switch checkedChildren="Sim" unCheckedChildren="Não" />
                </Form.Item>
            </Col>}                
            <Col xs={permitidoInserirRestrito ? 12 : 18} sm={7} md={8} lg={3}>
                <Form.Item
                    label={"Comprovante"}
                    name={["titulo", "comprovante"]}
                    rules={[{ required: false, max: 32 }]}
                >
                    <Input maxLength={32} placeholder="Comprovante" style={{ width: "100%" }} />
                </Form.Item>
            </Col>
            <Col xs={24} sm={permitidoInserirRestrito ? 11 : 14} md={permitidoInserirRestrito ? 10 : 13} lg={permitidoInserirRestrito ? 3 : 5}>
                <Form.Item label="Situação">
                    <TituloSituacaoSelect disabled value={isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo ? undefined : codSituacao} />
                </Form.Item>
            </Col>
            
            <Col xs={24}>
                <Row gutter={8}>
                    <Col xs={24} sm={8} md={4} lg={3}>
                        <Form.Item
                            label="Emissão"
                            name={["titulo", "dataEmissao"]}
                            rules={[{ required: true }]}
                        >
                            <InputDate />
                        </Form.Item>

                        <Form.Item
                            label="Vencimento"
                            name={isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo ? ["ignorar"] : ["titulo", "dataVencimento"]}
                            rules={isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo ? [] : [{ required: true }]}
                        >
                            <InputDate disabled={isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo ? true : disabled} />
                        </Form.Item>
                        
                        <Form.Item
                            label="Inclusão"
                            name={["titulo", "dataInclusao"]}
                            rules={[{ required: false }]}
                        >
                            <InputDate disabled />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8} md={4} lg={3}>
                        <Form.Item
                            label={"Valor Base"}
                            name={["titulo", "valorBase"]}
                            rules={[{ required: true }]}
                        >
                            <InputPreco placeholder="Base" />
                        </Form.Item>
                        <Form.Item
                            label={"Valor Desconto"}
                            name={["titulo", "valorDesconto"]}
                            rules={[{ required: false }]}
                        >
                            <InputPreco placeholder="Desconto" />
                        </Form.Item>
                        <Form.Item label="Previsto">
                            <InputPreco disabled value={valorPrevisto} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8} md={6} lg={5}>
                        <Form.Item name={"codCentroCusto"} label={"Centro de Custo"} rules={[{ required : !tituloRateiosFormValue?.some(any => any.centroCusto?.codigo !== undefined && any.centroCusto?.codigo > 0) }]}>
                            <CentroCustoSelectItem 
                                fechado={false}
                                disabled={tituloRateiosFormValue?.some(any => any.centroCusto?.codigo !== undefined && any.centroCusto?.codigo > 0) ? true : disabled}
                                onChange={(v) => {
                                    form.setFieldValue("codViagem", undefined);
                                }}
                                codCentroCustoInvalidarFiltro={initValuesRateios?.map(m => m?.codCentroCusto)?.filter(f => !!f)}
                            />
                        </Form.Item>
                        <Form.Item name={"codPlanoDeContas"} label={"Plano de Contas"} rules={[{ required : !tituloRateiosFormValue?.some(any => any.centroCusto?.codigo !== undefined && any.centroCusto?.codigo > 0) }]}>
                            <PlanoDeContasSelectItem
                                disabled={tituloRateiosFormValue?.some(any => any.centroCusto?.codigo !== undefined && any.centroCusto?.codigo > 0) ? true : disabled} 
                                filtros={{
                                    tipo : {
                                        codigo : EnumPlanoContasTipoMovimentacao.Receita
                                    },
                                    tipoRegistro : {
                                        codigo : EnumRegistroTipo.Item
                                    }
                                }}
                            />
                        </Form.Item>
                        {
                            codDevedorTipoFormValue === EnumPessoaTipo.RESPONSAVEL && <Form.Item
                                label="Inscrição"
                                name={"codInscricao"}
                            >
                                <InscricaoResponsavelSelect
                                    codResponsavel={codDevedorFormValue}
                                    codCentroDeCusto={rateios?.map(m => m.codCentroCusto)}
                                />
                            </Form.Item> || <Form.Item name={"codViagem"} label={"Viagem"}>
                                <ViagemSelect 
                                    codsCentrosDeCusto={rateios?.map(m => m.codCentroCusto)}
                                    disabled={(rateios?.length ?? 0) === 0 ? true : disabled}
                                    selectAutomatico={tituloRateiosFormValue?.some(any => any.centroCusto?.codigo !== undefined && any.centroCusto?.codigo > 0) ? false : true}
                                />
                            </Form.Item>
                        }
                    </Col>
                    <Col xs={24} sm={24} md={10} lg={13}>
                        <Row gutter={8}>
                            <Col xs={24} sm={8} md={12} lg={6}>
                                <Form.Item
                                    label="Dt. Pagamento"
                                    name={isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo ? ["ignorar"] : ["titulo", "dataPagamento"]}
                                    rules={[{ required: false }]}
                                >
                                    <InputDate
                                        disabled={isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo ? true : disabled}
                                        onChange={(v) => {
                                            if (v?.isValid())
                                            {
                                                if ((form.getFieldValue(["titulo", "valorRecebido"]) ?? 0) === 0)
                                                    form.setFieldValue(["titulo", "valorRecebido"], valorPrevisto);
                                                if ((form.getFieldValue(["titulo", "dataCredito"]) ?? undefined) === undefined)
                                                    form.setFieldValue(["titulo", "dataCredito"], v);
                                            }
                                            else 
                                            {
                                                form.setFieldValue(["titulo", "valorRecebido"], undefined);
                                                form.setFieldValue(["titulo", "dataCredito"], undefined);
                                                form.setFieldValue(["titulo", "movimentacao"], undefined);
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8} md={12} lg={6}>
                                <Form.Item
                                    label="Valor Pago"
                                    name={isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo ? ["ignorar"] : ["titulo", "valorRecebido"]}
                                    rules={[{ required: dataPagamentoInformada }]}
                                >
                                    <InputPreco placeholder="Valor Pago" disabled={!dataPagamentoInformada ? true : disabled} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8} md={12} lg={6}>
                                <Form.Item
                                    label="Conta"
                                    name={isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo ? ["ignorar"] : ["titulo", "movimentacao", "codConta"]}
                                    rules={[{ required: dataPagamentoInformada }]}
                                    style={{
                                        marginBottom : 0
                                    }}
                                >
                                    <ContaSelect 
                                        disabled={!dataPagamentoInformada ? true : disabled}
                                        filtros={{
                                            encerrada : false,
                                            codTiposInvalidos : rateiosPreenchidos ? [] : [EnumTipoConta.ADIANTAMENTO_VIAGEM],
                                            codCentrosDeCustosADV : rateiosPreenchidos ? rateios.filter(f => f?.codCentroCusto !== undefined && f.codCentroCusto > 0).map(m => m.codCentroCusto) : []
                                        }}
                                        onChange={(v) => {
                                            const newValue = Number(v);
                                            if (isFinite(newValue) && !isNaN(newValue) && newValue > 0)
                                            {
                                                form.setFieldValue(["titulo", "dataCredito"], tituloDataPagamentoFormValue);
                                            }
                                            else 
                                            {
                                                const valorRecebidoAntes = form.getFieldValue(["titulo", "valorRecebido"]);
                                                form.setFieldValue(["titulo", "dataCredito"], undefined);
                                                form.setFieldValue(["titulo", "movimentacao"], undefined);
                                                form.setFieldValue(["titulo", "valorRecebido"], valorRecebidoAntes);
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8} md={12} lg={6}>
                                <Form.Item
                                    label="Dt. Crédito"
                                    name={isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo ? ["ignorar"] : ["titulo", "dataCredito"]}
                                    rules={[{ required: false }]}
                                >
                                    <InputDate
                                        disabled={isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo ? true : disabled}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8} md={12} lg={9}>
                                <Form.Item 
                                    name={isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo ? ["ignorar"] : ["titulo", "movimentacao", "codMovFormaPagamento"]} 
                                    label="Forma" 
                                    rules={[{ required: dataPagamentoInformada }]}
                                >
                                    <MovFormaPagamentoSelect disabled={!dataPagamentoInformada ? true : disabled} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8} md={12} lg={5}>
                                <Form.Item
                                    style={{
                                        marginBottom : 0
                                    }}
                                    label="Juros/Multa"
                                    name={isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo ? ["ignorar"] : ["titulo", "movimentacao", "valorMulta"]}
                                    rules={[{ required: false }]}
                                >
                                    <InputPreco placeholder="Multa e Juros" disabled={!dataPagamentoInformada ? true : disabled} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8} md={12} lg={5}>
                                <Form.Item
                                    style={{
                                        marginBottom : 0
                                    }}
                                    label="Tarifa"
                                    name={(isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo) || codTituloMovFormaPagamentoFormValue === EnumMovFormaPagamento.Boleto ? ["ignorar"] : ["titulo", "movimentacao", "tituloTarifa", "valorRecebido"]}
                                    rules={[{ required: false }]}
                                >
                                    <InputPreco placeholder="Tarifa" disabled={!dataPagamentoInformada || codTituloMovFormaPagamentoFormValue === EnumMovFormaPagamento.Boleto ? true : disabled} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8} md={12} lg={5}>
                                <Form.Item
                                    label="Crédito"
                                >
                                    <InputPreco placeholder="Vl. Crédito" value={valorCredito} disabled />
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Form.Item name={["titulo", "descricao"]} label="Histórico" labelCol={{ style : { paddingBottom : 0, fontWeight : "bold" }}} rules={[{ max: 128 }]}>
                                    <Input.TextArea rows={1} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Col>

            <Col xs={24}>
                {children}
            </Col>
            <Col xs={24}>
                <Collapse
                    size="small"
                    destroyInactivePanel={false}
                    defaultActiveKey={disabled === true || isAlteracao ? ["parcelamento", "rateios", "info-pagamento"] : undefined}
                    items={[
                        {
                            key : "parcelamento",
                            label : "Parcelamento",
                            visivel : !isAlteracao && codTipoFormValue === EnumTituloTipo.Titulo,
                            children : <Row wrap gutter={8}>
                                <Col xs={24} sm={8} md={8} lg={4}>
                                    <Form.Item name={["parcelamento", "numeroRepeticao"]} label="Parcelas">
                                        <InputNumber />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8} md={8} lg={4}>
                                    <Form.Item name={["parcelamento", "diaParcela"]} rules={[{ required : numeroRepeticaoParcelamentoFormValue > 0 }]} label="Dia pagto.">
                                        <InputNumber />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8} md={8} lg={4}>
                                    <Form.Item label="Valor Parcela">
                                        <InputPreco disabled value={valorParcelado} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        },
                        {
                            key : "rateios",
                            label : "Rateio",
                            visivel : codCentroCustoFormValue === undefined && codPlanoDeContasFormValue === undefined,
                            children : <Form.List
                                name={["titulo", "rateios"]}
                                rules={[
                                    {
                                        async validator(_, value, __) {
                                            if (value === undefined || value === null || value.length == 0)
                                                return Promise.reject(new Error("Por favor, informe ao menos 1 rateio"));

                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                {(fields, { add, remove }, { errors, warnings }) => {
                                    return (
                                        <>
                                            <Button
                                                type="dashed"
                                                size="small"
                                                onClick={() => {
                                                    /// @ts-ignore
                                                    const percentualTotalRateado = rateios.reduce((acc, item) => acc + item.valor, 0);

                                                    if (percentualTotalRateado >= valorPrevisto) {
                                                        notification.info({
                                                            message : "Configuração do rateio já é equivalente a 100%"
                                                        });
                                                        return;
                                                    }

                                                    const novoRateio = {} as Rateio;
                                                    /// @ts-ignore
                                                    novoRateio.valor = valorPrevisto - percentualTotalRateado;
                                                    /// @ts-ignore
                                                    novoRateio.percentual = novoRateio.valor / valorPrevisto * 100;              
                                                    
                                                    add(novoRateio);
                                                }}
                                                icon={<PlusOutlined />}
                                            >
                                                rateio
                                            </Button>

                                            <Form.ErrorList warnings={warnings} errors={errors}></Form.ErrorList>

                                            {fields?.map(({ name, key }, index) => (
                                                <Row key={index.toString()} gutter={8} wrap style={{ width: "100%" }} align="bottom" justify={"start"}>
                                                    <Col xs={"auto"}>
                                                        <Form.Item>
                                                            <Button type="text"  onClick={() => {
                                                                remove(index);
                                                            }}>
                                                                <MinusCircleOutlined />
                                                            </Button>
                                                        </Form.Item>
                                                    </Col>
                                                    <Col flex="auto">
                                                        <TituloRaterioFormItem
                                                            isAReceber={false}
                                                            form={form}
                                                            nameBase={[name as any]}
                                                            namePath={["titulo", "rateios", name as any]}
                                                            valorTotalARatear={valorPrevisto}
                                                        />
                                                    </Col>
                                                </Row>
                                            ))}
                                        </>
                                    );
                                }}
                            </Form.List>
                        },
                        {
                            key : "info-pagamento",
                            label : "Informações pagamento",
                            visivel : codTituloMovFormaPagamentoFormValue !== undefined && codTituloMovFormaPagamentoFormValue !== EnumMovFormaPagamento.Dinheiro,
                            children : <Row wrap gutter={8}>
                                {
                                    codTituloMovFormaPagamentoFormValue !== EnumMovFormaPagamento.Boleto && <>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item
                                                name={["titulo", "movimentacao", "identificadorBanco"]}
                                                label="ID. Transaçao"
                                                rules={[{ max: 50 }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item name={["titulo", "movimentacao", "nsu"]} label="Nsu" rules={[{ max: 30 }]}>
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item name={["titulo", "movimentacao", "identificadorPagador"]} label="ID. Pagador" rules={[ { max : 30 } ]}>
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                    </>
                                }
                                {
                                    (
                                        codTituloMovFormaPagamentoFormValue === EnumMovFormaPagamento.Outros ||
                                        codTituloMovFormaPagamentoFormValue === EnumMovFormaPagamento.Credito ||
                                        codTituloMovFormaPagamentoFormValue === EnumMovFormaPagamento.Debito 
                                    ) && <>
                                        <Divider>Cartão</Divider>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item
                                                name={["titulo", "movimentacao", "codigoAutorizacao"]}
                                                label="Cód. Autorização"
                                                rules={[{ max: 30 }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item name={["titulo", "movimentacao", "bandeira"]} label="Bandeira" rules={[{ max: 30 }]}>
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        {(codTituloMovFormaPagamentoFormValue !== EnumMovFormaPagamento.Debito) && (
                                            <>
                                                <Col xs={24} sm={6} md={5} lg={4}>
                                                    <Form.Item name={["titulo", "movimentacao", "totalParcelas"]} label="Qtd. Parcelas">
                                                        <InputNumber style={{ width: "100%" }} />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={6} md={5} lg={4}>
                                                    <Form.Item name={["titulo", "movimentacao", "numeroParcela"]} label="Parcela">
                                                        <InputNumber style={{ width: "100%" }} />
                                                    </Form.Item>
                                                </Col>
                                            </>
                                        )}
                                    </>
                                }
                                {(
                                    codTituloMovFormaPagamentoFormValue === EnumMovFormaPagamento.Boleto
                                    || codTituloMovFormaPagamentoFormValue === EnumMovFormaPagamento.Outros
                                ) && (
                                    <>
                                        <Divider>Boleto</Divider>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item
                                                name={["titulo", "movimentacao", "nossoNumeroRetorno"]}
                                                label="Nº no banco"
                                                rules={[{ max: 30 }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item
                                                name={["titulo", "movimentacao", "nossoNumero"]}
                                                label="Nosso número"
                                                rules={[{ max: 30 }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item
                                                name={["titulo", "movimentacao", "numeroDocumento"]}
                                                label="Nº documento"
                                                rules={[{ max: 30 }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                    </>
                                )}
                                {
                                    (
                                        codTituloMovFormaPagamentoFormValue === EnumMovFormaPagamento.TransferenciaBancaria
                                        || codTituloMovFormaPagamentoFormValue === EnumMovFormaPagamento.Cheque
                                        || codTituloMovFormaPagamentoFormValue === EnumMovFormaPagamento.Outros
                                    ) && <>
                                        <Divider>Conta Externa</Divider>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item name={["titulo", "movimentacao", "bancoExterno"]} label="Banco" rules={[{ max: 3 }]}>
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item
                                                name={["titulo", "movimentacao", "agenciaExterna"]}
                                                label="Agência"
                                                rules={[{ max: 10 }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item
                                                name={["titulo", "movimentacao", "dacAgenciaExterna"]}
                                                label="Dig. agência"
                                                rules={[{ max: 2 }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item name={["titulo", "movimentacao", "contaExterna"]} label="Conta" rules={[{ max: 15 }]}>
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={6} md={5} lg={4}>
                                            <Form.Item
                                                name={["titulo", "movimentacao", "dacContaExterna"]}
                                                label="Dig. conta"
                                                rules={[{ max: 2 }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                    </>
                                }
                            </Row>
                        }
                    ].filter(f => (f.visivel ?? true) === true)}
                />
            </Col>
        </Row>
        <LancamentoVerificarInscricaoModal codInscricao={codInscricaoFormValue} receita={true} codTituloAtual={codTituloAtualFormValue} />
        {process.env.NODE_ENV === "development" && <Col xs={24}>
            <Form.Item shouldUpdate>
            {() => {
                return <>
                    <code>
                        Dados
                        <pre>{JSON.stringify({
                            receita: "Sim",
                            isAlteracao : isAlteracao ? "Sim" : "Não",
                            codFatura : codFaturaFormValue ?? 0,
                            codTitulo : tituloCodigoFormValue ?? 0,
                            dados: form.getFieldsValue(),
                        }, null, 2)}</pre>
                    </code>
                    <br />
                </> 
            }}
            </Form.Item>
        </Col>}
    </Form>;
});

export default AReceberLancamentoFormBasic;